import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, useTheme } from "@mui/material";
import * as Sentry from "@sentry/react";
import { Button, PageHeader, SectionHeader } from "@components/ui_v2";
import { CardResumenCotizacion, CardVehiculoCotizador } from "@components/ui_v2/cotizadoresComponents";
import { formatCurrencyWhole, getStaticResourceUrl } from "@helpers/funciones";
import { AUTO_WUWEI, RENTA_WUWEI } from "@pages/producto/credito_simple/wuwei/cotizacionWuWei";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { useLazyGetLeasingTypeConfigQuery } from "@api/catalogs";
import { TaxSystemConfigResponse } from "@interfaces/index";
import { QuoteVehicle } from "@interfaces/store";
import { LEASING_TYPE_CODES, PATHS_TOFU_V2 } from "@helpers/constantes";
import { TAX_SYSTEMS_CODES } from "@helpers/taxSystemsCodes";

const TAX_SYSTEM_CODE = TAX_SYSTEMS_CODES.WUWEI_PF;

/** Fotos de las unidades en los recursos estáticos de WuWei (`VITE_URL_STATIC_RESOURCES`, distinto por ambiente). */
const fotoAuto = (archivo: string) => getStaticResourceUrl(`wuwei/portal/autos/${archivo}`);

// Unidad con sus colores y fotos; los datos y la renta son los de `cotizacionWuWei` (los comparte la pre-oferta).
const AUTO = {
  ...AUTO_WUWEI,
  colores: [
    { id: "blanco", nombre: "Apricity White", hex: "#E9E3D2", img: fotoAuto("byd-dolphin-white.webp") },
    { id: "negro", nombre: "Polar Night Black", hex: "#1C1F23", img: fotoAuto("byd-dolphin-black.webp") },
    { id: "azul", nombre: "Azul Glaciar", hex: "#AEC6DD", img: fotoAuto("byd-dolphin-blue.webp") },
  ],
};
const RENTA = RENTA_WUWEI;

/**
 * Cotizador de WuWei (`WW_Cotizador` del diseño): arrendamiento de auto para conductores de plataforma. No hay monto
 * ni plazo que ajustar: se elige el color de la unidad y se muestra la renta semanal con su desglose.
 */
const CotizadorWuWei = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [getLeasingTypeConfig] = useLazyGetLeasingTypeConfigQuery();
  const [config, setConfig] = useState<TaxSystemConfigResponse | null>(null);
  const [leasingTypeId, setLeasingTypeId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [colorId, setColorId] = useState(AUTO.colores[0].id);

  const getInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      const taxSystemConfig = await getTaxSystemConfig(TAX_SYSTEM_CODE).unwrap();
      setConfig(taxSystemConfig);
      // Arrendamiento de auto: el tipo vehicular de la config del tax system, como LaniAuto y Vivemoto.
      const leasingTypes = await getLeasingTypeConfig({ tax_system_id: taxSystemConfig.id }).unwrap();
      setLeasingTypeId(
        leasingTypes.find((item) => item.leasing_type.code === LEASING_TYPE_CODES.VEHICULAR)?.leasing_type_id ?? "",
      );
    } catch (error) {
      // Mismo alcance que los otros cotizadores: sin config no se puede continuar, sin modal de error.
      Sentry.captureException(error, { extra: { component: "CotizadorWuWei-initial_fetch" } });
    } finally {
      setIsLoading(false);
    }
  }, [getTaxSystemConfig, getLeasingTypeConfig]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  // Igual que los otros cotizadores: el código de origen de la liga se guarda para la creación de cuenta.
  useEffect(() => {
    const sourceCode = new URLSearchParams(window.location.search).get("code");
    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  // Mismos parámetros que los otros cotizadores, con el precio de la unidad como monto y su plazo en meses.
  // TODO: regresar a `account_type=phone` (cuenta con el celular) cuando el backend ajuste el registro; mientras,
  // la cuenta se crea con el correo.
  // La unidad y el color elegidos viajan como `vehicle` (JSON), con el residual: "Tus datos" los guarda en el leasing.
  const handleSolicitar = () => {
    if (!config?.product_id || !leasingTypeId) return;
    const sourceCode = sessionStorage.getItem("source_code");
    const vehicle: QuoteVehicle = {
      vehicle_type: "Autos",
      make: AUTO.marca,
      model: AUTO.modeloLeasing,
      version: AUTO.version,
      year: AUTO.anio,
      color: AUTO.colores.find((color) => color.id === colorId)?.nombre ?? "",
      condition: "Nuevo",
    };
    let urlPortal = `${PATHS_TOFU_V2.crearCuenta.path}?tax_system_id=${config.id}&product_id=${config.product_id}&person_type=${config.code}&loan_amount=${AUTO.precio}&loan_term=${AUTO.plazoMeses}&tax_system_type=${config.tax_system_type}&account_type=phone&leasing_type_id=${leasingTypeId}&residual_percentage=${AUTO.residualPct}&vehicle=${encodeURIComponent(JSON.stringify(vehicle))}`;
    if (sourceCode) {
      urlPortal += `&source_code=${sourceCode}`;
    }
    navigate(urlPortal);
  };

  return (
    <Box>
      <PageHeader
        variant="cotizador"
        eyebrow="Cotización · Arrendamiento de auto"
        title="Maneja tu propio auto"
        subtitle="Elige el color de tu unidad y ajusta tu plazo. Al final del contrato, el auto puede ser tuyo con la opción a compra."
      />

      <SectionHeader
        variant="underline"
        label="Tu unidad"
        meta="Nueva · 100% eléctrica · seguro, mantenimiento y GPS incluidos"
      />

      <CardVehiculoCotizador
        nombre={`${AUTO.marca} ${AUTO.modelo}`}
        meta={`${AUTO.anio} · ${AUTO.tipo} · ${AUTO.autonomia} de autonomía`}
        colores={AUTO.colores}
        value={colorId}
        onChange={setColorId}
        testId="cotizador-wuwei-color"
      />

      <CardResumenCotizacion
        label="Pago semanal"
        // La tarjeta va en el dorado de la marca (`accent`), no en el azul marino del botón.
        brandColor={theme.palette.primary.accent}
        amount={formatCurrencyWhole(RENTA.semanal)}
        amountFontSize={30}
        caption={`MXN / semana (IVA incluido) durante ${AUTO.plazoMeses} meses · con opción a compra al final`}
        disclaimer={`Todo incluido: seguro todo riesgo, mantenimiento preventivo, GPS y gestión vehicular. Sin enganche, únicamente una renta en garantía de ${formatCurrencyWhole(RENTA.garantia)} que se te devuelve al final del contrato. La renta se cobra por descuento automático de tus ganancias en la plataforma. Oferta final sujeta a aprobación.`}
        actions={
          <Button
            tone="hero"
            withArrow
            onClick={handleSolicitar}
            disabled={isLoading || !config?.product_id || !leasingTypeId}
            data-testid="cotizador-wuwei-continue-button"
          >
            Solicitar mi auto
          </Button>
        }
      >
        {/* Desglose de la renta semanal (`quote-hero-main` del diseño). */}
        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: "5px", maxWidth: 320 }}>
          {RENTA.desglose.map((item) => (
            <Box key={item.label} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
              <Typography sx={{ fontSize: 12.5, color: "tokens.textTertiary" }}>{item.label}</Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "tokens.textPrimary" }}>
                {formatCurrencyWhole(item.value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardResumenCotizacion>
    </Box>
  );
};

export default CotizadorWuWei;
